import { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { useWeeklyProgress } from '../../../../api/hooks/useWeeklyProgress';
import { PmInfoBox } from '../../../../components/pm';

interface WeeklyProgress {
  id: string;
  kw: number;
  jahr: number;
  neu: number;
  offen: number;
  wip: number;
  erledigt: number;
  snapshot_date: string;
}

/** Segment-Reihenfolge wie Clickdummy: von unten Neu → Offen → WIP → Erledigt. */
const SEGMENTS: Array<{ key: 'neu' | 'offen' | 'wip' | 'erledigt'; label: string; color: string }> = [
  { key: 'neu', label: 'Neu', color: 'var(--pm-petrol-150)' },
  { key: 'offen', label: 'Offen', color: 'var(--pm-petrol-300)' },
  { key: 'wip', label: 'In Arbeit', color: 'var(--pm-petrol-500)' },
  { key: 'erledigt', label: 'Erledigt', color: 'var(--pm-petrol-750)' },
];

/**
 * Arbeitsfortschritt über die Wochen-Snapshots — gestapelter Balken
 * (Neu/Offen/WIP/Erledigt) aus echten weekly-progress-Daten.
 */
export function DashboardWorkChart({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { data } = useWeeklyProgress(projectId);

  const items = useMemo(
    () => ((data as unknown as { data?: WeeklyProgress[] } | undefined)?.data ?? []),
    [data],
  );

  const chartData = useMemo(
    () =>
      [...items]
        .sort((a, b) => a.jahr - b.jahr || a.kw - b.kw)
        .map((p) => ({
          name: `KW${p.kw}/${String(p.jahr).slice(-2)}`,
          neu: p.neu ?? 0,
          offen: p.offen ?? 0,
          wip: p.wip ?? 0,
          erledigt: p.erledigt ?? 0,
        })),
    [items],
  );

  if (chartData.length === 0) {
    return (
      <PmInfoBox variant="info" title={t('dashboard.workChartEmpty', 'Noch keine Snapshots')}>
        {t(
          'dashboard.workChartEmptyHint',
          'Sobald im Wochenfortschritt Snapshots angelegt werden, baut sich der Verlauf hier auf.',
        )}
      </PmInfoBox>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="var(--pm-bg-deep)" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--pm-fg-subtle)' }} />
          <YAxis tick={{ fontSize: 11, fill: 'var(--pm-fg-subtle)' }} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              background: 'var(--pm-bg-subtle)',
              border: '1px solid var(--pm-bg-deep)',
              borderRadius: 8,
              fontSize: '0.8rem',
            }}
          />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          {SEGMENTS.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} stackId="work" fill={s.color} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
